import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import Dropdown from "../../../components/Dropdown";
import { useSelector, useDispatch } from "react-redux";
import { ThunkDispatch, AnyAction } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import {
  getStatistics,
  setFilterQuery,
  setGlobalTalentStatus,
  setGlobalTalentStatusDateRank,
} from "../../../reducers/companyTalent";

interface Option {
  name: string;
  id: string | number;
}

interface TalentStatistics {
  date_options: Option[];
  total: number;
  total_registered: number;
  total_scout: number;
  total_cv_screened: number;
  total_interviewed: number;
  total_change_job_success: number;
}

interface RootState {
  companyTalent: {
    talentStatistics: TalentStatistics;
  };
}

interface SummaryInfo {
  id: number;
  title: string;
  quantity: number;
  talent_filter_field: string;
  isSelected: boolean;
}

type IconType = 'plus' | 'arrow' | 'none';
type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

const PlusIcon = memo(() => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    aria-hidden="true"
  >
    <g clipPath="url(#clip0_12523_11763)">
      <path
        d="M19 13.25H13V19.25H11V13.25H5V11.25H11V5.25H13V11.25H19V13.25Z"
        fill="#323232"
      />
    </g>
    <defs>
      <clipPath id="clip0_12523_11763">
        <rect
          width="24"
          height="24"
          fill="white"
          transform="translate(0 0.25)"
        />
      </clipPath>
    </defs>
  </svg>
));

const ArrowIcon = memo(() => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M8.58984 7.66L13.1698 12.25L8.58984 16.84L9.99984 18.25L15.9998 12.25L9.99984 6.25L8.58984 7.66Z"
      fill="#808080"
    />
  </svg>
));

const useTalentStatistics = (defaultTimeOption: Option) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedOption, setSelectedOption] = useState<Option>(defaultTimeOption);
  const talentStats = useSelector((state: RootState) => state.companyTalent.talentStatistics);
  const rankAOnly = useSelector((state: any) => state.companyHome.rankAOnly);
  const selectedTypeOfTalent = useSelector((state: any) => state.companyHome.selectedTypeOfTalent);

  const fetchStatistics = useCallback((timeOption: Option, rankAOnly, selectedTypeOfTalent) => {
    dispatch(getStatistics({ 
      time: String(timeOption.id),
      search: '',
      rankAOnly: rankAOnly ? 1 : 0,
      talentType: selectedTypeOfTalent?.value,
    }));
  }, [dispatch]);

  useEffect(() => {
    fetchStatistics(selectedOption, rankAOnly, selectedTypeOfTalent);
  }, [fetchStatistics, selectedOption, rankAOnly, selectedTypeOfTalent]);

  const handleOptionChange = useCallback((option: Option) => {
    setSelectedOption(option);
    dispatch(setGlobalTalentStatusDateRank(option));
  }, []);

  return {
    talentStats,
    selectedOption,
    handleOptionChange,
  };
};

// TalentSummary Component
interface TalentSummaryProps {
  filter: SummaryInfo;
  iconType: IconType;
  onNavigate: (filterField: string) => void;
}

const TalentSummary = memo(({ filter, iconType }: TalentSummaryProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const rankAOnly = useSelector((state: any) => state.companyHome.rankAOnly);
  const selectedTypeOfTalent = useSelector((state: any) => state.companyHome.selectedTypeOfTalent);

  const navigateToTalentList = () => {
    const customFilter = {
      ...filter,
      type_of_talent: selectedTypeOfTalent && selectedTypeOfTalent?.value ? [selectedTypeOfTalent] : [],
      recruitment_rank: rankAOnly ? { name: 'A', id: 'a' } : null,
      withdrawn_talent_included: true,
    }
    dispatch(setGlobalTalentStatus(customFilter));
    navigate("/company/talent", { state: { isFromDetail: false } });
  };

  const renderIcon = () => {
    switch (iconType) {
      case 'plus':
        return <PlusIcon />;
      case 'arrow':
        return <ArrowIcon />;
      case 'none':
      default:
        return null;
    }
  };

  return (
    <>
      <button
        className={`${
          filter.isSelected ? "bg-[#FF861A] text-white" : ""
        } rounded-lg p-2 hover:bg-[#FF861A] hover:text-white group`}
        onClick={navigateToTalentList}
        aria-label={`View ${filter.title} talents`}
      >
        <span className="text-sm font-bold leading-[1.125rem] block text-left">
          {filter.title}
        </span>
        <span
          className={`border-b border-[#FF861A] ${
            filter.isSelected ? "text-white" : "alu-text-base-2"
          }`}
        >
          <strong className="group-hover:text-white text-[2rem] font-bold leading-[3rem] tracking-wide">
            {filter.quantity}
          </strong>
          <span className="group-hover:text-white text-xl font-bold leading-[1.125rem] tracking-tight">
            {filter.title === "登録数" ? "人" : "件"}
          </span>
        </span>
      </button>
      {renderIcon()}
    </>
  );
});

TalentSummary.displayName = 'TalentSummary';

// Main Component
const CompanyHomeSummary: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const defaultTimeOption: Option = { name: "過去30日間", id: 'day_30' };

  useEffect(() => {
    dispatch(setGlobalTalentStatusDateRank(defaultTimeOption));
  }, []);

  const {
    talentStats,
    selectedOption,
    handleOptionChange
  } = useTalentStatistics(defaultTimeOption);

  const talentSummaryData = useMemo<SummaryInfo[]>(() => [
    { id: 1, title: "登録数", quantity: talentStats?.total || 0, talent_filter_field: "action_registration", isSelected: false },
    { id: 2, title: "スカウト", quantity: talentStats?.total_scout || 0, talent_filter_field: "action_new_scout", isSelected: false },
    { id: 3, title: "応募", quantity: talentStats?.total_registered || 0, talent_filter_field: "action_new_apply", isSelected: false },
    { id: 4, title: "書類選考", quantity: talentStats?.total_cv_screened || 0, talent_filter_field: "action_selecting", isSelected: false },
    { id: 5, title: "面接", quantity: talentStats?.total_interviewed || 0, talent_filter_field: "interview", isSelected: false },
    { id: 6, title: "採用", quantity: talentStats?.total_change_job_success || 0, talent_filter_field: "action_offer_accepted", isSelected: false }
  ], [talentStats]);

  const getIconType = useCallback((index: number, totalItems: number): IconType => {
    if (index === 1) return 'plus';
    if (index === totalItems - 1) return 'none';
    return 'arrow';
  }, []);

  return (
    <div className="mb-6 rounded-2xl bg-white p-6 alu-section-box-shadow">
      <header className="pb-2 flex gap-8 items-center">
        <h3 className="text-base font-bold alu-text-base-2">選考状況</h3>
        <div className="shrink-0 min-w-[160px]">
          <Dropdown
            inputWidth="full"
            fontWeight="bold"
            type="text"
            options={talentStats?.date_options || []}
            value={selectedOption}
            onChange={handleOptionChange}
          />
        </div>
      </header>

      <div className="flex gap-10 items-center">
        {talentSummaryData.map((filter, index) => (
          <TalentSummary
            key={filter.id}
            filter={filter}
            iconType={getIconType(index, talentSummaryData.length)}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(CompanyHomeSummary);
